import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import {
  NOT_COMPARED_REASONS,
  type BaseDeviationLine,
  type BaseDeviationNotCompared,
  type BaseDeviationParagraph,
  type BaseDeviationReport as Report,
  type BaseDeviationSideCaveats,
  type BaseDeviationSideCounts,
  type TextSpan,
} from '@clm/shared-types';

import { getBaseDeviationReport } from '../api/base-deviation-reports';
import { retrieveContract } from '../api/contracts';
import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { ErrorNotice } from '../components/ErrorNotice';
import { requireSurface } from '../routes/surfaces';
import {
  NOTE_SOURCE_COPY,
  NOT_COMPARED_COPY,
  REPORT_SECTIONS,
  baseDeviationReportPath,
  codelessHeadingOf,
  lineCopyOf,
  matchReviewPath,
} from './base-deviation-shared';

/**
 * One base-MSA deviation report, re-opened — CLM-112 (FR-38, NFR-18, DR-11,
 * R-112-8).
 *
 * The report is shown **as computed**: the snapshot the run stored, never a
 * recompute under the same name. Beside it, the live `now` block says what has
 * moved since — the base replaced, either contract's text re-converted, the
 * paragraph matches changed — as banners above everything else, so nobody reads
 * a stale comparison as today's.
 *
 * **Coverage first** (NFR-18): before any difference, how much of each side was
 * compared, and why the rest was not — the reasons, the text no paragraph
 * covers, and the caveats (tables not detected, processing-restricted,
 * amendments not included, spans the API could not verify). A report that led
 * with its findings would read partial as complete.
 *
 * **Slices are rendered here, from `retrieve-contract include: ['full_text']`**
 * for each contract, behind a disclosure — the report itself carries spans and
 * no text (rule 13). A side whose text changed since the run is never sliced:
 * its stored spans index text that has moved (R-112-8). The flags are re-read
 * *after* the text is fetched, not trusted from when the page opened: a tab
 * left open can outlive a re-conversion, and `retrieve-contract` carries no
 * `converted_utc` to compare with the run's pin.
 *
 * **The subject is the report's own**, never the URL's: the GET is by report id
 * alone, so a URL naming another contract is redirected to the report's
 * subject rather than showing one contract's findings under another's name.
 *
 * Every quoted `deviation_note` is labelled **against the library clause** —
 * it is the matcher's or a reviewer's words about a paragraph and the master
 * text, never about the base — with its source and evidence row (R-112-15).
 */
type Phase =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly report: Report }
  | { readonly kind: 'problem'; readonly problem: ApiProblemKind; readonly correlation_id: string | null };

type TextState =
  | { readonly kind: 'unasked' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly base: string | null; readonly subject: string | null }
  | { readonly kind: 'problem'; readonly problem: ApiProblemKind };

export function BaseDeviationReport() {
  const { contract_key: contractKey, report_id: reportId } = useParams<{ contract_key: string; report_id: string }>();
  const surface = requireSurface('detail');
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  useEffect(() => {
    if (reportId === undefined) return;
    const controller = new AbortController();
    setPhase({ kind: 'loading' });
    void getBaseDeviationReport(reportId, controller.signal).then(
      (report) => {
        if (!controller.signal.aborted) setPhase({ kind: 'loaded', report });
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        setPhase({
          kind: 'problem',
          problem: classifyApiError(error),
          correlation_id: error instanceof ApiError ? error.correlation_id : null,
        });
      },
    );
    return () => {
      controller.abort();
    };
  }, [reportId]);

  if (phase.kind === 'loaded' && contractKey !== phase.report.subject_contract_key) {
    /* An edited or mistyped URL: the report is about its own subject, so it is shown there — every "this
     * contract" on the page, and the record link, would otherwise name a contract the findings are not about. */
    return <Navigate to={baseDeviationReportPath(phase.report.subject_contract_key, phase.report.report_id)} replace />;
  }
  /* Until the report is read, the URL is only where the reader came from. */
  const recordKey = phase.kind === 'loaded' ? phase.report.subject_contract_key : contractKey;

  return (
    <>
      <div className="hd">
        <div>
          <h2>Base MSA deviation report</h2>
          <p>
            {surface.label} ·{' '}
            {recordKey === undefined ? null : (
              <Link to={`/contracts/record/${encodeURIComponent(recordKey)}`}>
                <code>{recordKey}</code>
              </Link>
            )}
          </p>
        </div>
      </div>
      {phase.kind === 'loading' ? (
        <div className="card" role="status" aria-busy="true">
          <p>Loading report {reportId}…</p>
        </div>
      ) : null}
      {phase.kind === 'problem' ? <ErrorNotice kind={phase.problem} correlation_id={phase.correlation_id} /> : null}
      {phase.kind === 'loaded' ? (
        <Loaded report={phase.report} onReread={(report) => setPhase({ kind: 'loaded', report })} />
      ) : null}
    </>
  );
}

function titleOf(report: Report, code: string | null): string | null {
  if (code === null) return null;
  return report.now.clause_titles.find((title) => title.clause_code === code)?.title ?? null;
}

/** How many distinct paragraphs on one side sit on a `provisional` line (R-112-6) — each awaits a person's decision. */
function provisionalParagraphs(report: Report, side: 'base' | 'subject'): number {
  const ids = new Set<string>();
  for (const line of report.result.lines) {
    for (const paragraph of side === 'base' ? line.base_paragraphs : line.subject_paragraphs) {
      if (paragraph.provisional) ids.add(paragraph.contract_clause_id);
    }
  }
  return ids.size;
}

function Loaded({ report, onReread }: { readonly report: Report; readonly onReread: (report: Report) => void }) {
  const { result, now } = report;
  const [text, setText] = useState<TextState>({ kind: 'unasked' });
  const textAbort = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      textAbort.current?.abort();
    },
    [],
  );

  /**
   * Both contracts' text, once, behind the disclosure — never for a side whose text moved (R-112-8).
   *
   * A side already flagged is not fetched. The report is then read again **after** the text arrives, and a side
   * whose flag is set now is dropped: `converted_utc` never returns to the run's pin once it moves, so a text that
   * differs from the one the run sliced shows as moved on that second read, however long the tab was open.
   * `retrieve-contract` carries no `converted_utc` of its own to compare with. The fresh `now` also replaces the
   * page's, so its banners say what the slices were withheld for.
   */
  function loadText() {
    if (text.kind !== 'unasked') return;
    const controller = new AbortController();
    textAbort.current = controller;
    setText({ kind: 'loading' });
    const read = async (contractKey: string, changed: boolean): Promise<string | null> =>
      changed ? null : (await retrieveContract(contractKey, ['full_text'], controller.signal)).full_text;
    void (async () => {
      const [base, subject] = await Promise.all([
        read(report.base.contract_key, now.text_changed.base),
        read(report.subject_contract_key, now.text_changed.subject),
      ]);
      const reread = await getBaseDeviationReport(report.report_id, controller.signal);
      return { reread, base, subject };
    })().then(
      ({ reread, base, subject }) => {
        if (controller.signal.aborted) return;
        onReread(reread);
        setText({
          kind: 'ready',
          base: reread.now.text_changed.base ? null : base,
          subject: reread.now.text_changed.subject ? null : subject,
        });
      },
      (error: unknown) => {
        if (!controller.signal.aborted) setText({ kind: 'problem', problem: classifyApiError(error) });
      },
    );
  }

  const texts = text.kind === 'ready' ? text : null;

  return (
    <>
      <div className="card" data-testid="bdr-header">
        <h3>
          Computed against base MSA v{report.base.version_no} (<code>{report.base.contract_key}</code>)
        </h3>
        <p data-testid="bdr-subject">
          Subject: <code>{report.subject_contract_key}</code> — “this contract” below is this one.
        </p>
        <p className="mut">
          Run by {report.requested_by_upn} at {report.computed_utc}. Report {report.report_id} · result SHA-256{' '}
          <code>{report.result_sha256.slice(0, 12)}…</code>, as the audit trail records it.
        </p>
        {now.nomination_is_current ? null : (
          <p className="banner up" role="status" data-testid="bdr-superseded">
            The base MSA has been replaced since this report was computed
            {now.base_superseded_utc === null ? '' : ` (v${String(report.base.version_no)} was superseded at ${now.base_superseded_utc})`}. This
            report still shows the comparison against v{report.base.version_no}. Run it again from the contract record to
            compare with the current base.
          </p>
        )}
        {now.text_changed.subject || now.text_changed.base ? (
          <p className="banner up" role="status" data-testid="bdr-text-changed">
            {[now.text_changed.subject ? 'this contract' : null, now.text_changed.base ? 'the base MSA' : null]
              .filter((who) => who !== null)
              .join(' and ')}{' '}
            has been re-converted since this report was computed. Its stored paragraph spans index text that has moved, so
            its paragraphs are not shown. The findings below are as they were computed.
          </p>
        ) : null}
        {now.matches_changed ? (
          <p className="banner up" role="status" data-testid="bdr-matches-changed">
            Paragraph matches have changed since this report was computed (a review, a re-match or a library revision). The
            findings below are as they were computed; run the report again to see the comparison today.
          </p>
        ) : null}
        <p className="mut">
          A join through the clause library, not a redline: no edited document, no tracked change and no counter-proposal
          is produced. No model was called.
        </p>
      </div>

      <div className="card" data-testid="bdr-coverage">
        <h3>What was compared</h3>
        <Coverage side="base" label="base MSA" counts={result.summary.base} caveats={result.caveats.base} />
        <Coverage side="subject" label="this contract" counts={result.summary.subject} caveats={result.caveats.subject} />
        {result.summary.version_skewed_lines === 0 ? null : (
          <p className="mut">
            {result.summary.version_skewed_lines} paired clause{result.summary.version_skewed_lines === 1 ? ' is' : 's are'} matched
            to different versions of the library clause on each side.
          </p>
        )}
      </div>

      <div className="card">
        <details className="disclose" onToggle={loadText}>
          <summary>Show the paragraphs’ text</summary>
          {text.kind === 'loading' ? <p role="status">Fetching both contracts’ converted text…</p> : null}
          {text.kind === 'problem' ? <ErrorNotice kind={text.problem} /> : null}
          <p className="mut">
            Read from each contract’s converted text, on demand. The report stores spans, never text; values are
            highlighted where the value gate compared them.
          </p>
        </details>
      </div>

      {REPORT_SECTIONS.map((section) => {
        const lines = result.lines.filter((line) => (section.categories as readonly string[]).includes(line.category));
        const body = (
          <>
            {lines.length === 0 ? <p className="mut">None.</p> : null}
            {lines.map((line, index) => (
              <LineView
                key={`${line.category}-${line.clause_code ?? ''}-${String(index)}`}
                line={line}
                title={titleOf(report, line.clause_code)}
                baseText={texts?.base ?? null}
                subjectText={texts?.subject ?? null}
              />
            ))}
          </>
        );
        return (
          <div className="card" key={section.id} data-testid={`bdr-section-${section.id}`}>
            {section.collapsed ? (
              <details>
                <summary>
                  <strong>{section.heading}</strong> ({lines.length})
                </summary>
                {body}
              </details>
            ) : (
              <>
                <h3>
                  {section.heading} ({lines.length})
                </h3>
                {body}
              </>
            )}
          </div>
        );
      })}

      <div className="card" data-testid="bdr-not-compared">
        <h3>Not compared</h3>
        <NotComparedList
          label="Base MSA"
          contractKey={report.base.contract_key}
          entries={result.not_compared.base}
          provisional={provisionalParagraphs(report, 'base')}
        />
        <NotComparedList
          label="This contract"
          contractKey={report.subject_contract_key}
          entries={result.not_compared.subject}
          provisional={provisionalParagraphs(report, 'subject')}
        />
      </div>
    </>
  );
}

function Coverage({
  side,
  label,
  counts,
  caveats,
}: {
  readonly side: 'base' | 'subject';
  readonly label: string;
  readonly counts: BaseDeviationSideCounts;
  readonly caveats: BaseDeviationSideCaveats;
}) {
  const reasons = NOT_COMPARED_REASONS.filter((reason) => reason !== 'not_segmented_text' && counts.not_compared_by_reason[reason] > 0);
  return (
    <div data-testid={`bdr-coverage-${side}`}>
      <p>
        <strong>
          Compared {counts.compared} of {counts.paragraphs} {label} paragraphs; {counts.not_compared} not compared.
        </strong>
      </p>
      {reasons.length === 0 ? null : (
        <ul>
          {reasons.map((reason) => (
            <li key={reason}>
              {counts.not_compared_by_reason[reason]} — {NOT_COMPARED_COPY[reason]}
            </li>
          ))}
        </ul>
      )}
      {counts.uncovered_chars === 0 ? null : (
        <p className="mut">
          {counts.uncovered_chars} characters of text no paragraph covers ({counts.not_compared_by_reason.not_segmented_text} stretch
          {counts.not_compared_by_reason.not_segmented_text === 1 ? '' : 'es'}).
        </p>
      )}
      <span className="fl">
        {caveats.conversion_caveat === 'tables_missing' ? <span className="tag acc">Tables not detected</span> : null}
        {caveats.processing_restricted ? <span className="tag bad">Processing restricted — withheld paragraphs not compared</span> : null}
        {caveats.amendments_not_included === 0 ? null : (
          <span className="tag acc">
            {caveats.amendments_not_included} amendment{caveats.amendments_not_included === 1 ? '' : 's'} not included
          </span>
        )}
        {caveats.spans_unverified === 0 ? null : (
          <span className="tag acc">{caveats.spans_unverified} paragraph spans could not be verified</span>
        )}
      </span>
    </div>
  );
}

function LineView({
  line,
  title,
  baseText,
  subjectText,
}: {
  readonly line: BaseDeviationLine;
  readonly title: string | null;
  readonly baseText: string | null;
  readonly subjectText: string | null;
}) {
  const codeless = codelessHeadingOf(line);
  return (
    <div className="bdr-line" data-testid="bdr-line" data-category={line.category} data-matched-by={line.matched_by}>
      <p>
        {codeless !== null ? (
          <strong>{codeless}</strong>
        ) : (
          <>
            <code>{line.clause_code}</code> {title === null ? null : <strong>{title}</strong>}
          </>
        )}{' '}
        {line.provisional ? (
          <span className="tag acc" title="An unreviewed MEDIUM match — a person should decide.">
            Provisional
          </span>
        ) : null}
        {line.confirmed === false ? (
          <span className="tag acc" title="The other side has paragraphs that could not be compared, and one of them could be this clause.">
            Unconfirmed — {line.other_side_unresolved} entr{line.other_side_unresolved === 1 ? 'y' : 'ies'} on the other side not compared
          </span>
        ) : null}
      </p>
      <p className="mut">
        {lineCopyOf(line)}
        {line.values === null ? null : line.values.equal ? ' Values equal.' : ' Values differ.'}
        {line.clause_versions.base.length === 0 && line.clause_versions.subject.length === 0
          ? null
          : ` Library versions: base ${versions(line.clause_versions.base)}, this contract ${versions(line.clause_versions.subject)}.`}
      </p>
      <div className="bdr-sides">
        <Side label="Base MSA" paragraphs={line.base_paragraphs} text={baseText} marks={line.values?.base_spans ?? []} />
        <Side label="This contract" paragraphs={line.subject_paragraphs} text={subjectText} marks={line.values?.subject_spans ?? []} />
      </div>
      {line.notes.map((note) => (
        <blockquote key={`${note.side}-${note.contract_clause_id}`} className="bdr-note" data-testid="bdr-note">
          “{note.deviation_note}”
          <footer className="mut">
            {note.side === 'base' ? 'Base MSA' : 'This contract'}, against the library clause — from {NOTE_SOURCE_COPY[note.source]}
            {note.audit_id === null ? '; no evidence row was found for it' : <>; audit row <code>{note.audit_id}</code></>}.
          </footer>
        </blockquote>
      ))}
    </div>
  );
}

function versions(list: readonly number[]): string {
  return list.length === 0 ? '—' : list.map((v) => `v${String(v)}`).join(', ');
}

function Side({
  label,
  paragraphs,
  text,
  marks,
}: {
  readonly label: string;
  readonly paragraphs: readonly BaseDeviationParagraph[];
  readonly text: string | null;
  readonly marks: readonly TextSpan[];
}) {
  if (paragraphs.length === 0) return null;
  return (
    <div>
      <span className="mr-excerpt-label">{label}</span>
      {paragraphs.map((paragraph) => (
        <div key={paragraph.contract_clause_id}>
          <span className="mut">
            ¶ {paragraph.paragraph_ref ?? '—'} · {paragraph.match_state ?? 'no match state'}
            {paragraph.reviewed ? ' · reviewed' : ''}
          </span>
          {text === null ? null : <Slice text={text} span={paragraph.span} marks={marks} />}
        </div>
      ))}
    </div>
  );
}

/** One paragraph's slice of `full_text`, with the value spans that fall inside it marked. */
function Slice({ text, span, marks }: { readonly text: string; readonly span: TextSpan; readonly marks: readonly TextSpan[] }) {
  const inside = marks
    .filter((mark) => mark.start_offset >= span.start_offset && mark.end_offset <= span.end_offset)
    .sort((a, b) => a.start_offset - b.start_offset);
  const parts: JSX.Element[] = [];
  let cursor = span.start_offset;
  for (const mark of inside) {
    if (mark.start_offset < cursor) continue;
    parts.push(<span key={`t${String(cursor)}`}>{text.slice(cursor, mark.start_offset)}</span>);
    parts.push(<mark key={`m${String(mark.start_offset)}`}>{text.slice(mark.start_offset, mark.end_offset)}</mark>);
    cursor = mark.end_offset;
  }
  parts.push(<span key={`t${String(cursor)}`}>{text.slice(cursor, span.end_offset)}</span>);
  return (
    <blockquote className="mr-excerpt" data-testid="bdr-slice">
      {parts}
    </blockquote>
  );
}

/**
 * One side's not-compared entries — and the way to settle them. The Match review link shows whenever the side has
 * anything a reviewer could decide: a not-compared entry, **or** a provisional match (R-112-6). A provisional
 * paragraph is compared, so it is never an entry here, yet the review surfaces are its remedy too; without this a
 * side with provisional lines and nothing uncompared would offer no path to settle them.
 */
function NotComparedList({
  label,
  contractKey,
  entries,
  provisional,
}: {
  readonly label: string;
  readonly contractKey: string;
  readonly entries: readonly BaseDeviationNotCompared[];
  readonly provisional: number;
}) {
  return (
    <div data-testid="bdr-not-compared-side">
      <h4>
        {label} ({entries.length})
      </h4>
      {entries.length === 0 ? (
        <p className="mut">Everything on this side was compared.</p>
      ) : (
        <ul>
          {entries.map((entry) =>
            'paragraph' in entry ? (
              <li key={entry.paragraph.contract_clause_id} data-testid="bdr-not-compared-entry" data-reason={entry.reason}>
                ¶ {entry.paragraph.paragraph_ref ?? '—'} — {NOT_COMPARED_COPY[entry.reason]}
              </li>
            ) : (
              <li key={`text-${String(entry.span.start_offset)}`} data-testid="bdr-not-compared-entry" data-reason={entry.reason}>
                {entry.char_count} characters at offset {entry.span.start_offset} — {NOT_COMPARED_COPY[entry.reason]}
              </li>
            ),
          )}
        </ul>
      )}
      {provisional === 0 ? null : (
        <p className="mut">
          {provisional} paragraph{provisional === 1 ? ' on this side is a provisional match' : 's on this side are provisional matches'} — an
          unreviewed MEDIUM match a person should decide.
        </p>
      )}
      {entries.length === 0 && provisional === 0 ? null : (
        <p>
          <Link to={matchReviewPath(contractKey)}>Review {contractKey}’s paragraph matches</Link> — each decision there settles a
          provisional match or shrinks what a new report cannot compare.
        </p>
      )}
    </div>
  );
}
